import React, { useCallback, useMemo, useState } from "react";
import BackLink from "../components/BackLink";
import { Alert, Button, Col, Container, Modal, Row } from "react-bootstrap";
import { ReactComponent as IconPhone } from "../assets/icon-phone.svg";
import JourneyProgress from "../components/JourneyProgress";
import { useGetUnitListQuery } from "../features/rapidstor/rapidstorApiSlice";
import { useLoaderData, useNavigate } from "react-router-dom";
import Location from "../features/rapidstor/entities/Location";
import Descriptor from "../components/UnitListing/Descriptor";
import classes from "./UnitListing.module.scss";
import { setUnitData } from "../store/bookingSlice";
import store from "../store";
import QuoteForm from "../components/UnitListing/QuoteForm";
import { useAppliedFilters } from "../hooks/useAppliedFilters";
import { Link } from "react-router-dom";
import UnitType from "../features/rapidstor/entities/UnitType";
import DescriptorT from "../features/rapidstor/entities/Descriptor";
import { useEffect } from "react";
import ReactSimplyCarousel from "../components/UnitListing/Carousel";
import Countdown from "../components/Countdown";

interface SelectedDescriptorType {
  [descriptorId: string]: string;
}

function UnitListing() {
  const location = useLoaderData() as Location;
  const navigate = useNavigate();

  const [carouselIndex, setCarouselIndex] = useState<number>(0);
  const [quoteModalOpen, setQuoteModalOpen] = useState<boolean>(false);
  const [defaultSelected, setDefaultSelected] = useState<number | undefined>(undefined);
  const [unitTypes] = useState<SelectedDescriptorType>({});
  const [unitAvailable, setUnitAvailable] = useState<boolean>(false);

  const {
    data: unitList,
    isLoading,
    error,
    refetch,
  } = useGetUnitListQuery(location.locationCode);

  const { filters } = useAppliedFilters();

  const descriptorList = useMemo(() => {
    if (!unitList) {
      return [];
    }
    const filtersIncludeDescriptor = (descriptor: DescriptorT) => {
      return descriptor.unitTypes.some((unitType) => {
        return Object.entries(filters).every(([filter, value]) => {
          switch (filter) {
            case "sTypeName.re":
              if ((value as string).charAt(0) === "!") {
                return !new RegExp((value as string).substring(1), "i").test(
                  unitType.typeName
                );
              }
              return new RegExp(value as string, "i").test(unitType.typeName);
          }
          return true;
        });
      });
    };

    return unitList.descriptors.filter(filtersIncludeDescriptor).sort((a, b) => {
      return a.area - b.area;
    });
  }, [unitList, filters]);

  const isDescriptorAvailable = useCallback(
    (index: number) => {
      const descriptor = descriptorList[index];
      const unitTypeId = unitTypes[descriptor?._id];
      const available: number =
        descriptor?.unitTypes.reduce((acc: number, unitType: UnitType) => {
          if (unitTypeId && unitType._id !== unitTypeId) {
            return acc;
          }
          return acc + unitType.totalAvailable;
        }, 0) ?? 0;
      return available > 0;
    },
    [descriptorList, unitTypes]
  );

  useEffect(() => {
    if (!descriptorList.length) {
      return;
    }
    const scoreDescriptor = (descriptor: DescriptorT): number => {
      return Math.max(
        ...descriptor.unitTypes.map((unitType) => {
          return Object.entries(filters)
            .map(([filter, value]) => {
              switch (filter) {
                case "dcArea.min":
                  return unitType.area - (value as number);
                case "dcLength.min":
                  return Math.max(
                    unitType.length - (value as number),
                    unitType.width - (value as number)
                  );
                case "bInside":
                  return unitType.inside === value ? 1 : -10;
                case "sTypeName.re":
                  return new RegExp(value as string, "i").test(unitType.typeName)
                    ? 1
                    : -10;
                default:
                  console.warn('Unknown filter "' + filter + '"');
              }
              return 1;
            })
            .reduce((acc, cur) => {
              if (cur < 0 && acc < 0) {
                return cur + acc;
              } else if (cur > 0 && acc > 0) {
                return cur + acc;
              } else {
                return Math.min(cur, acc);
              }
            }, 0);
        })
      );
    };

    const descriptorsScored = descriptorList
      .map((descriptor, index) => {
        return [index, scoreDescriptor(descriptor)] as [number, number];
      })
      .sort((a, b) => {
        // Return smallest positives, then largest negatives
        if (a[1] > 0 && b[1] > 0) {
          return a[1] - b[1];
        }
        return b[1] - a[1];
      });
    const firstAvailable = descriptorsScored.find(([index, score]) => {
      return isDescriptorAvailable(index) && score > 0;
    });
    if (firstAvailable) {
      setCarouselIndex(firstAvailable[0]);
      setDefaultSelected(firstAvailable[0]);
    } else {
      setCarouselIndex(descriptorsScored[0][0]);
      setDefaultSelected(descriptorsScored[0][0]);
    }
  }, [descriptorList, filters, isDescriptorAvailable]);

  function goToBookingForm(
    unitType: UnitType,
    descriptor: Descriptor,
    reservation: boolean
  ) {
    store.dispatch(setUnitData({ location, unitType, descriptor, reservation }));
    navigate(
      reservation
        ? `/locations/${location.slug}/reserve`
        : `/locations/${location.slug}/book`
    );
  }

  function onBookUnitClicked() {
    const descriptor = descriptorList[carouselIndex];
    const unitType = descriptor?.unitTypes.find(
      (unitType) => unitType._id === unitTypes[descriptor._id]
    );
    if (descriptor && unitType) {
      goToBookingForm(unitType, descriptor, false);
    }
  }

  const onEmailQuoteClicked = useCallback(() => {
    const descriptor = descriptorList[carouselIndex];
    const unitType = descriptor?.unitTypes.find(
      (unitType) => unitType._id === unitTypes[descriptor._id]
    );
    if (descriptor && unitType) {
      store.dispatch(
        setUnitData({
          location,
          unitType,
          descriptor,
          reservation: false,
        })
      );
      setQuoteModalOpen(true);
    }
  }, [carouselIndex, descriptorList, location, unitTypes]);

  const onUnitTypeSelected = useCallback(
    (descriptor: DescriptorT, unitType: UnitType) => {
      unitTypes[descriptor._id] = unitType._id;
      setUnitAvailable(isDescriptorAvailable(carouselIndex));
    },
    [unitTypes, carouselIndex, isDescriptorAvailable]
  );

  const shouldShowDescriptor = (index: number) => {
    if (
      carouselIndex === index ||
      carouselIndex === index + 1 ||
      carouselIndex === index - 1 ||
      carouselIndex === index + 2 ||
      carouselIndex === index - 2
    ) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <>
      <Container className={"phone-container listing-container"}>
        <Container>
          <BackLink desktop />
        </Container>
        <Container>
          <Row>
            <Col xs={12}>
              <JourneyProgress mobile className={"mt-2"} />
              <Countdown minutes={20} seconds={0} />
              {!location ? null : isLoading || defaultSelected === undefined ? (
                "Unit list is loading"
              ) : !descriptorList || error ? (
                <Alert className={"mt-4"} variant={"secondary"}>
                  Sorry, we were unable to load the list of units.{" "}
                  <div className={"mt-2"}>
                    <Button onClick={refetch} className={"ms-2"} variant={"secondary"}>
                      Retry
                    </Button>
                  </div>
                </Alert>
              ) : (
                <>
                  <Container
                    className={classes.results_header}
                    style={{
                      maxWidth: "900px",
                    }}
                  >
                    <h3>Your Results</h3>
                  </Container>
                  <Container
                    style={{
                      maxWidth: "900px",
                    }}
                  >
                    <ReactSimplyCarousel
                      containerProps={{
                        style: {
                          width: "100%",
                          userSelect: "none",
                          marginLeft: "-12px",
                          marginTop: "20px",
                        },
                      }}
                      swipeTreshold={60}
                      activeSlideIndex={carouselIndex}
                      activeSlideProps={{
                        className: "active-slide",
                        style: {
                          borderRadius: "4px",
                        },
                      }}
                      onRequestChange={setCarouselIndex}
                      forwardBtnProps={{
                        style: {
                          marginTop: "20px",
                          opacity: 0.5,
                          border: 0,
                          background: "transparent",
                          color: "rgb(0, 0, 0, 1)",
                          alignSelf: "center",
                        },
                      }}
                      backwardBtnProps={{
                        style: {
                          marginTop: "20px",
                          opacity: 0.5,
                          border: 0,
                          background: "transparent",
                          color: "rgb(0, 0, 0, 1)",
                          alignSelf: "center",
                        },
                      }}
                      dotsNav={{
                        show: true,
                        itemBtnProps: {
                          style: {
                            height: 8,
                            width: 8,
                            padding: 0,
                            borderRadius: "50%",
                            border: 0,
                            margin: "0 2px",
                            background: "rgba(0, 0, 0, 0.2)",
                          },
                        },
                        activeItemBtnProps: {
                          style: {
                            height: 8,
                            width: 8,
                            padding: 0,
                            borderRadius: "50%",
                            border: 0,
                            margin: "0 2px",
                            // background: "#ee3823",
                          },
                        },
                      }}
                      itemsToShow={5}
                      speed={300}
                      centerMode
                      infinite={descriptorList.length > 2 ? true : false}
                    >
                      {descriptorList.map((descriptor: DescriptorT, index: number) => {
                        const shouldShow = shouldShowDescriptor(index);
                        return (
                          <div
                            key={descriptor._id}
                            className={classes.descriptor_wrapper}
                            style={{
                              opacity: shouldShow ? 1 : 0,
                            }}
                          >
                            <Descriptor
                              onBookUnitClicked={onBookUnitClicked}
                              onEmailQuoteClicked={onEmailQuoteClicked}
                              onUnitTypeSelected={onUnitTypeSelected}
                              location={location}
                              descriptor={descriptor}
                              recommended={index === defaultSelected}
                            />
                          </div>
                        );
                      })}
                    </ReactSimplyCarousel>
                  </Container>
                </>
              )}
            </Col>
          </Row>
        </Container>
      </Container>
      {location &&
      !isLoading &&
      descriptorList &&
      !error &&
      defaultSelected !== undefined ? (
        <>
          <div className={`${classes.bottomPanel} border-top`}>
            <Container className={"phone-container"}>
              <Row>
                <Col xs={6}>
                  <Button
                    variant={"outline-primary"}
                    className={"btn-md w-100"}
                    onClick={onEmailQuoteClicked}
                  >
                    Email Quote
                  </Button>
                </Col>
                {unitAvailable ? (
                  <Col xs={6}>
                    <Button className={"btn-md w-100"} onClick={onBookUnitClicked}>
                      Book Unit
                    </Button>
                  </Col>
                ) : (
                  <Col xs={6}>
                    <Link to={`/contact/${location.slug}`}>
                      <Button className={"btn-md w-100"}>
                        Call for Availability{" "}
                        <IconPhone className={"ms-2 d-none d-sm-inline"} />
                      </Button>
                    </Link>
                  </Col>
                )}
              </Row>
            </Container>
          </div>
          <Modal show={quoteModalOpen} onHide={() => setQuoteModalOpen(false)} centered>
            <Modal.Body>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => setQuoteModalOpen(false)}
              ></button>
              <QuoteForm />
            </Modal.Body>
          </Modal>
        </>
      ) : null}
    </>
  );
}

export default UnitListing;
